import {RegistrationPathService} from './registration-path-service';
import {UserInfoService} from './user-info-service';
import {ProductGroupService} from './product-group-service';
import {PixelService} from './pixel-service';
import {ApiService} from './api-service';
import {AppCallService} from './app-call-service';
import {UnityAppCall} from '@aoflmkt/app-call';
import {AdjustService} from './adjust-service';
import {CacheManager, cacheTypeEnumerate} from '@aofl/cache-manager';
import {routerInstance} from '@aofl/router';
import {queryParamKeys, cacheNamespaces, adjustTokens, productGroups, pixelEvents, appCallCallbacks, subscriberTypes, pages} from './constants-enumerate';
import {SentryService} from './sentry-service';

/**
 * PayPal Response Handler
 *
 * @class PayPalService
 */
class PayPalService {
  /**
   * Grabs all query parameters returned in PayPal's response url
   *
   */
  static getQueryParams() {
    const queryParams = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((val, key) => {
      queryParams[key] = val;
    });

    return queryParams;
  }

  /**
   * Grabs cached user data set from the original subscription/reactivation page that the user intiated a
   * paypal purchase request from. This object's keys will vary depending on which page the data is set from,
   * but subscriberType and originPage must always be set.
   */
  static getUserData() {
    const payPalUserData = new CacheManager(cacheNamespaces.PAYPAL_USER_DATA, cacheTypeEnumerate.LOCAL);
    return payPalUserData.getItem(cacheNamespaces.PAYPAL_USER_DATA);
  }

  /**
   *
   * @param {Object} userData
   * @param {Object} queryParams
   */
  static createApiPayload(userData, queryParams) {
    return {
      ...userData,
      billingInfo: queryParams,
    };
  }

  /**
   *
   */
  static clearUserData() {
    const payPalUserData = new CacheManager(cacheNamespaces.PAYPAL_USER_DATA, cacheTypeEnumerate.LOCAL);
    payPalUserData.clear();
  }

  /**
   *
   * Makes the Adjust tracking app call
   * @private
   */
  static adjustLogEvent(eventToken) {
    UnityAppCall.addListener(this.adjustEventCallback.bind(this));
    AppCallService.adjustEvent(eventToken);
  }

  /**
   * @param {String} email
   */
  static parseEmailDomain(email) {
    return email.split('@')[1];
  }

  /**
   *
   * @param {Object} payload
   * @private
   */
  static async subscribeUser(payload) {
    try {
      const subscribeResp = await ApiService.subscribe(payload);

      if (subscribeResp.payload.invitation_data) {
        const riqCodeData = new CacheManager(cacheNamespaces.RIQ_CODE_DATA, cacheTypeEnumerate.SESSION);
        riqCodeData.setItem(cacheNamespaces.RIQ_CODE_DATA, {
          riqCode: subscribeResp.payload.invitation_data.riq
        });
      }

      ProductGroupService.setProductGroupStandardRegPath(subscribeResp.payload.login_info);
      UserInfoService.setUserInfo(subscribeResp.payload.login_info);
      await PixelService.fire(pixelEvents.CONVERSION);

      AdjustService.track(adjustTokens.SUBSCRIPTION, this.adjustEventCallback.bind(this));
      RegistrationPathService.next();
    } catch (err) {
      SentryService.captureException(err);
    }
  }

  /**
   *
   * @param {Object} payload
   * @private
   */
  static async resubscribeUser(payload) {
    try {
      const resubscribeResp = await ApiService.resubscribe(payload);

      if (resubscribeResp.payload.invitation_data) {
        const riqCodeData = new CacheManager(cacheNamespaces.RIQ_CODE_DATA, cacheTypeEnumerate.SESSION);
        riqCodeData.setItem(cacheNamespaces.RIQ_CODE_DATA, {
          riqCode: resubscribeResp.payload.invitation_data.riq
        });
      }

      ProductGroupService.setProductGroupStandardRegPath(resubscribeResp.payload.login_info);
      UserInfoService.setUserInfo({
        user_uuid: resubscribeResp?.payload?.login_info?.user_uuid,
        user_account_type: resubscribeResp?.payload?.login_info?.user_account_type,
        step: resubscribeResp?.payload?.login_info?.step,
        is_active: resubscribeResp?.payload?.login_info?.is_active,
        token_valid: true,
        is_reactivated: resubscribeResp?.payload?.login_info?.is_reactivated
      });

      const isAnnual = (this.product === productGroups.ANNUAL_7999);
      const eventToken = isAnnual ? adjustTokens.REACTIVATION_ANNUAL : adjustTokens.REACTIVATION_MONTHLY;

      AdjustService.track(eventToken, this.adjustEventCallback.bind(this));
      RegistrationPathService.next();
    } catch (err) {
      SentryService.captureException(err);
    }
  }

  /**
   *
   * @private
   * @param {Object} data
   * @return {Boolean}
   */
  static adjustEventCallback(data) {
    if (typeof data !== 'object' || typeof data.request === 'undefined' || data.request !== appCallCallbacks.ADJUST_EVENT) {
      return false;
    }
    UnityAppCall.addListener(this.adjustEventCallback)();
    RegistrationPathService.next();
  }

  /**
   *
   * @param {String} userData The user data set from the original subscription/reactivation page.
   */
  static handleCancel(userData) {
    const originPage = userData.originPage;
    const redeemCode = userData?.accessCode;
    const queryString = userData?.queryString;
    let page = redeemCode ? pages[originPage] + redeemCode : pages[originPage];
    if (queryString) {
      page = page + queryString;
    }
    routerInstance.navigate(page);
  }

  /**
   * Entry point for the PayPalService.
   *
   */
  static async processResponse() {
    const userData = this.getUserData();
    const queryParams = this.getQueryParams();
    const payload = this.createApiPayload(userData, queryParams);
    if (queryParams[queryParamKeys.PAYPAL_STATUS] === 'cancel') return this.handleCancel(userData);

    SentryService.addBreadcrumb({
      category: 'subscription.context.paypal',
      data: {
        originPage: userData?.originPage,
        subscriberType: userData?.subscriberType
      },
      level: SentryService.severity.Info
    });

    if (userData.subscriberType === subscriberTypes.SUBSCRIPTION) {
      await this.subscribeUser(payload);
    } else if (userData.subscriberType === subscriberTypes.REACTIVATION) {
      await this.resubscribeUser(payload);
    }
  }
}

export {
  PayPalService
};
